import {AfterViewInit, Component, computed, ElementRef, input, InputSignal, OnDestroy, output, signal, ViewChild} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {AppControlErrorComponent} from '@shared/components/forms/app-control-error/app-control-error.component';
import {BaseAppControlValueAccessorComponent} from '@shared/components/forms/app-control-value-accessor/base-app-control-value-accessor.component';
import {NgClass} from '@angular/common';
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {debounceTime, distinctUntilChanged, Subject, takeUntil} from 'rxjs';
import {NgxMaskDirective, provideNgxMask} from 'ngx-mask';

export type IconPosition = 'left' | 'right';

@Component({
    selector: 'app-currency',
    templateUrl: './app-currency.component.html',
    styleUrls: ['./app-currency.component.scss'],
    standalone: true,
    imports: [ReactiveFormsModule, FormsModule, TranslateModule, AppControlErrorComponent, NgClass, NgbTooltip, NgxMaskDirective],
    providers: [provideNgxMask()]
})
export class AppCurrencyComponent extends BaseAppControlValueAccessorComponent implements AfterViewInit, OnDestroy {
    @ViewChild('inputElement', {static: false}) inputElement?: ElementRef<HTMLInputElement>;

    // Inputs específicos do componente
    containerStyles = input<string>('');
    groupIcon: InputSignal<string> = input('', {transform: (value: string) => this.replaceTablerIconPrefix(value)});
    groupIconPosition = input<IconPosition>('left');
    label = input<string>('');
    labelTooltip = input<string>('');
    placeholder = input<string>('');
    hint = input<string>('');
    customErrors = input<Record<string, string>>({});
    readonly = input<boolean>(false);
    showErrorDescription = input<boolean>(true);
    autocomplete = input<string>('off');

    // Configuração da máscara
    mask = input<string>('separator.2');
    thousandSeparator = input<string>('.');
    separatorLimit = input<string>('9999999999');
    prefix = input<string>('R$ ');

    // Novos inputs para debounce
    debounceTime = input<number>(300); // Tempo em ms para debounce (padrão 300ms)

    // Outputs
    onClick = output<void>();
    valueChange = output<any>(); // Output para mudanças com debounce

    // Estado interno do componente
    private isFocused = signal<boolean>(false);
    private destroy$ = new Subject<void>();
    private inputSubject = new Subject<any>();

    // Computed properties
    hasIcon = computed(() => !!this.groupIcon());
    isLeftIcon = computed(() => this.hasIcon() && this.groupIconPosition() === 'left');
    isRightIcon = computed(() => this.hasIcon() && this.groupIconPosition() === 'right');

    inputClasses = computed(() => {
        const classes = ['form-control'];

        if (this.isLeftIcon()) classes.push('input-icon-left');
        if (this.isRightIcon()) classes.push('input-icon-right');

        return classes.join(' ');
    });

    containerClasses = computed(() => {
        const classes = ['form-group'];
        if (this.containerStyles()) classes.push(this.containerStyles());
        if (this.isFocused()) classes.push('focused');
        return classes.join(' ');
    });

    constructor() {
        super();
    }

    // Propriedade para binding com ngModel no template
    get currentValue(): any {
        return this.getValue();
    }

    set currentValue(value: any) {
        if (value !== this.getValue()) {
            this.writeValue(value);
        }
    }

    ngAfterViewInit(): void {
        this.setupControlListener();
        this.setupDebounce();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupDebounce(): void {
        this.inputSubject.pipe(
            debounceTime(this.debounceTime()),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe(value => {
            this.setValue(value);
            this.valueChange.emit(value);
        });
    }

    // Event handlers específicos do input
    onFocus(): void {
        this.isFocused.set(true);
        this.markAsTouched();
    }

    onBlur(): void {
        this.isFocused.set(false);
    }

    handleInput(event: Event): void {
        const target = event.target as HTMLInputElement;
        const value = this.parseValue(target.value);

        if (this.debounceTime() > 0) {
            // Com debounce - usa o subject
            this.inputSubject.next(value);
        } else {
            // Sem debounce - atualiza imediatamente
            this.setValue(value);
            this.valueChange.emit(value);
        }
    }

    private parseValue(rawValue: string): any {
        if (!rawValue) return null;

        // Remove o prefixo R$ e espaços
        let cleanValue = rawValue.replace(/R\$\s?/g, '');

        // Remove os separadores de milhar (.)
        cleanValue = cleanValue.replace(/\./g, '');

        // Substitui vírgula por ponto para conversão
        cleanValue = cleanValue.replace(',', '.');

        const numberValue = parseFloat(cleanValue);

        return isNaN(numberValue) ? null : numberValue;
    }

    // Métodos utilitários específicos do input
    focus(): void {
        this.inputElement?.nativeElement.focus();
    }

    blur(): void {
        this.inputElement?.nativeElement.blur();
    }

    select(): void {
        this.inputElement?.nativeElement.select();
    }
}

